import { db } from '@/mocks/db';
import type { TimeClockCorrection } from '@/mocks/db/schema';
import type { ClockingCorrectionRow } from '../-types';
import { timeToHHmm } from '../-utils/draft';

/** clocking-record デモと同一組織。 */
export const CLOCKING_CORRECTION_ORG_ID = 'org-demo-001';
function monthPrefix(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * 修正対象になり得る年月（最新の勤怠がある月）を返す。未指定アクセス時の初期表示に使う。
 */
export async function getCorrectableLatestPeriod(employeeId: string): Promise<{
  year: number;
  month: number;
} | null> {
  const records = await db.attendanceRecords.filter(r => r.employeeId === employeeId && r.organizationId === CLOCKING_CORRECTION_ORG_ID).toArray();
  let best: {
    y: number;
    m: number;
  } | null = null;
  for (const r of records) {
    if (r.date.length < 7) continue;
    const y = Number(r.date.slice(0, 4));
    const m = Number(r.date.slice(5, 7));
    if (!Number.isFinite(y) || !Number.isFinite(m)) continue;
    if (!best || y > best.y || y === best.y && m > best.m) {
      best = {
        y,
        m
      };
    }
  }
  return best ? {
    year: best.y,
    month: best.m
  } : null;
}

/**
 * 指定従業員・年月の日次勤怠を、打刻修正テーブルの行に変換して返す（Dexie デモ）。
 * 休憩時間は AttendanceRecord に存在しないため元値 0 とし、従業員が修正入力する。
 */
export async function getClockingCorrectionRows(employeeId: string, year: number, month: number): Promise<ClockingCorrectionRow[]> {
  const prefix = monthPrefix(year, month);
  const raw = await db.attendanceRecords.filter(r => r.employeeId === employeeId && r.organizationId === CLOCKING_CORRECTION_ORG_ID && r.date.startsWith(prefix)).toArray();
  return raw.sort((a, b) => a.date.localeCompare(b.date)).map(record => {
    const [y, mo, d] = record.date.split('-').map(Number);
    return {
      rowKey: record.date,
      recordId: record.id,
      date: new Date(y ?? 1970, (mo ?? 1) - 1, d ?? 1),
      workplaceName: record.workplaceName,
      attendanceStatus: record.status,
      original: {
        clockIn: timeToHHmm(record.actualStart),
        clockOut: timeToHHmm(record.actualEnd),
        breakMinutes: 0
      }
    } satisfies ClockingCorrectionRow;
  });
}

/**
 * 打刻修正の一括申請を Dexie に書き込む（status='pending'）。人事承認待ちとして保存される。
 */
export async function submitTimeClockCorrections(records: TimeClockCorrection[]): Promise<void> {
  if (records.length === 0) return;
  await db.timeClockCorrections.bulkAdd(records);
}